import {
  Component,
  Input,
  OnInit,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
  OnDestroy,
  Inject,
} from "@angular/core";
import { AppConstants } from "src/app/constants/constants";
import { DigLitSAConstants, generateGUID } from "src/app/constants/DigitalLiteracyConstants";
import { APP_CONFIG } from "src/app/lib/angular/injection-tokens";
import { IAppConfig } from "src/app/lib/config/app-config";

@Component({
  selector: "app-audio",
  templateUrl: "./audio.component.html",
  styleUrls: ["./audio.component.scss"],
})
export class AudioComponent implements OnInit, OnChanges, OnDestroy {
  @Input() src: string = AppConstants.BLANK;
  @Input() autoplay: boolean = false;
  @Input() queue: string[] = [];
  @Input() forceMute: boolean = false;
  @Input() forcePause: string = AppConstants.BLANK;
  @Output() onForceMute: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() onForcePause: EventEmitter<string> = new EventEmitter<string>();

  audioElement: HTMLAudioElement | null = null;
  isPlaying: boolean = false;
  mute: boolean = false;
  play: boolean = false;
  rootAudioPath: string = AppConstants.BLANK;
  queued: boolean = false;
  queueList: string[] = [];
  paused: boolean = false;
  myID: string = AppConstants.BLANK;

  muteTooltip: string = DigLitSAConstants.MUTE_AUDIO;
  unmuteTooltip: string = DigLitSAConstants.UNMUTE_AUDIO;
  playTooltip: string = DigLitSAConstants.PLAY_AUDIO;
  pauseTooltip: string = DigLitSAConstants.PAUSE_AUDIO;
  replayTooltip: string = DigLitSAConstants.REPLAY_AUDIO;
  trimmed: boolean = false;
  constructor(
    @Inject(APP_CONFIG) private config: IAppConfig
  ) {
    if (this.config.scormContentLocation) {
      this.rootAudioPath = this.config.scormContentLocation;
    }
  }

  ngOnInit(): void {

    if (this.src != AppConstants.BLANK) {
      if (this.autoplay) {
        this.playAudio(this.src);
      } else {
        this.initAudioElement();
      }
    }
    if (this.queue && this.queue.length > 0) {
      this.queueList = [...this.queue];
    }
    this.myID = generateGUID();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes && changes[AppConstants.FORCE_MUTE]) {
      if (this.audioElement) {
        this.audioElement.muted = changes[AppConstants.FORCE_MUTE].currentValue;
      }
    }
    if (changes && changes[AppConstants.FORCE_PAUSE]) {
      if(changes[AppConstants.FORCE_PAUSE].currentValue != this.myID) {
        this.paused = true;
        this.audioElement.pause();
      }
    }
  }

  ngOnDestroy(): void {
    if (this.audioElement) {
      this.audioElement.pause();
      this.audioElement.ontimeupdate = null;
      this.audioElement.onended = null;
      this.audioElement = null;
    }
  }

  initAudioElement(): void {
    if (!this.audioElement) {
      this.audioElement = new Audio();
      this.audioElement.onended = () => {
        this.playNextFromQueue();
      };
    }
  }

  playAudio(src: string, replay = false): void {
    if (replay) {
      this.queueList = [...this.queue];
      this.audioElement.currentTime = 0;
      this.queued = false;
    }

    const fullSrc = `${this.rootAudioPath}${src}`;

    if (!this.audioElement) {
      this.initAudioElement();
      this.audioElement.currentTime = this.autoplay ? 2 : 5;
    } else {
      if (this.audioElement.src.includes(fullSrc) && this.paused) {
        this.audioElement.play();
        this.onForcePause.emit(this.myID);
        this.trimmed = false;
        return;
      }

      if (
        this.queued &&
        this.paused &&
        this.queue.map(r => r.split(AppConstants.SLASH).pop()).includes(
          this.audioElement.src.split(AppConstants.SLASH).pop()
        ) &&
        (this.audioElement.paused && this.audioElement.currentTime != this.audioElement.duration)
      ) {
        this.audioElement.play();
        this.onForcePause.emit(this.myID);
        this.paused = false;
        this.trimmed = false;
        return;
      }
    }

    this.audioElement.src = fullSrc;
    this.isPlaying = true;

    this.audioElement.ontimeupdate = () => {
      if (!this.audioElement) return;

      const remainingTime =
        this.audioElement.duration - this.audioElement.currentTime;
      if (remainingTime < 5 && !this.trimmed) {
        this.audioElement.currentTime = this.audioElement.duration;
        this.trimmed = true;
      }
    };

    this.audioElement
      .play()
      .then(() => { 
        this.onForcePause.emit(this.myID); 
        this.trimmed = false; 
      })
      .catch((_) => {
        this.isPlaying = false;
      });
  }

  playNextFromQueue(): void {
    if (this.queueList.length > 0) {
      const nextSrc = this.queueList.shift() as string;
      this.queued = true;
      this.playAudio(nextSrc);
    } else {
      this.queued = false;
      this.audioElement.pause();
      this.isPlaying = false;
      if (this.queue && this.queue.length > 0) {
        this.queueList = [...this.queue];
      }
      this.audioElement.currentTime = 0;
    }
  }

  pauseAudio(): void {
    if (this.audioElement) {
      this.audioElement.pause();
      this.paused = true;
    }
  }

  cleanupAudio(): void {
    this.audioElement = new Audio();
  }

  toggleMute(): void {
    if (this.audioElement) {
      this.audioElement.muted = !this.audioElement.muted;

      if (this.autoplay) {
        this.onForceMute.emit(this.audioElement.muted);
      }
    }
  }
}
